import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Check, AlertTriangle, Home, LogOut } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const AbleResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(true);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const passwordValid = password.length >= 8;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleGoToDashboard = () => {
    navigate('/chat');
  };

  const handleContinueWithReset = async () => {
    await logout();
    setShowAuthModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!passwordValid) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Something went wrong. Please try again.');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Missing or invalid token
  if (!token) {
    return (
      <div className="min-h-screen flex">
        <div className="hidden md:flex md:w-[42%] bg-[#0B1120] flex-col justify-between p-12 relative overflow-hidden">
          <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#C6F135] rounded-full opacity-[0.07] blur-3xl" />
          <div className="relative z-10">
            <img src="/able_logo_white.png" alt="Able" className="h-8 mb-16" />
          </div>
          <div className="relative z-10 flex space-x-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#C6F135]" />
            <div className="w-1.5 h-1.5 rounded-full bg-[#C6F135] opacity-60" />
            <div className="w-1.5 h-1.5 rounded-full bg-[#C6F135] opacity-30" />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
          <div className="w-full max-w-[400px] text-center">
            <div className="mx-auto w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mb-5">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
            </div>
            <h2 className="font-['Satoshi'] text-2xl font-bold text-gray-900 tracking-tight">
              Invalid reset link
            </h2>
            <p className="mt-3 text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <div className="mt-8">
              <Link
                to="/forgot-password"
                className="inline-flex items-center justify-center py-3 px-6 text-sm font-semibold rounded-[0.75rem] text-white bg-[#2ECC71] hover:bg-[#27AE60] transition-colors"
              >
                Request a new link
              </Link>
            </div>
            <div className="mt-4">
              <Link to="/login" className="inline-flex items-center text-sm font-medium text-[#2ECC71] hover:text-[#27AE60] transition-colors">
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Authentication Modal */}
      {isAuthenticated && showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-[1rem] shadow-2xl border border-gray-100 p-8 max-w-md mx-4">
            <div className="text-center">
              <div className="mx-auto w-14 h-14 bg-[#ECFDF5] rounded-full flex items-center justify-center mb-5">
                <Home className="h-6 w-6 text-[#2ECC71]" />
              </div>
              <h3 className="font-['Satoshi'] text-xl font-bold text-gray-900 mb-2 tracking-tight">
                You're already signed in
              </h3>
              <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                You can go to your dashboard or continue to reset your password (this will sign you out of your current session).
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleGoToDashboard}
                  className="w-full flex items-center justify-center py-3 px-4 border border-[#D4D4D8] text-sm font-semibold rounded-[0.75rem] text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <Home className="mr-2 h-4 w-4" />
                  Take me to the dashboard
                </button>
                <button
                  onClick={handleContinueWithReset}
                  className="w-full flex items-center justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-[0.75rem] text-white bg-[#2ECC71] hover:bg-[#27AE60] transition-colors"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Go to reset password (logout)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Left Panel - Hero Dark */}
      <div className="hidden md:flex md:w-[42%] bg-[#0B1120] flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#C6F135] rounded-full opacity-[0.07] blur-3xl" />
        <div className="absolute top-1/3 -right-20 w-48 h-48 bg-[#C6F135] rounded-full opacity-[0.05] blur-2xl" />

        <div className="relative z-10">
          <img src="/able_logo_white.png" alt="Able" className="h-8 mb-16" />
          <h1 className="font-['Satoshi'] text-4xl font-bold text-white tracking-tight leading-tight">
            {success ? 'All set!' : 'Choose a new password'}
          </h1>
          <p className="mt-4 text-[#A1A1AA] text-lg leading-relaxed max-w-sm">
            {success
              ? 'Your password has been updated. You can now sign in with your new password.'
              : 'Make it strong and memorable.'}
          </p>
        </div>

        <div className="relative z-10 flex space-x-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-[#C6F135]" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#C6F135] opacity-60" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#C6F135] opacity-30" />
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="md:hidden mb-10">
            <img src="/able-logo.svg" alt="Able" className="h-7 mb-6" />
          </div>

          {!success ? (
            <>
              <h2 className="font-['Satoshi'] text-2xl font-bold text-gray-900 tracking-tight">
                Set new password
              </h2>
              <p className="mt-2 text-gray-500 text-sm">
                Your new password must be at least 8 characters.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                {/* New Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                    New password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full px-4 py-3 pr-11 border border-[#D4D4D8] rounded-[0.75rem] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2ECC71] focus:ring-1 focus:ring-[#2ECC71] transition-colors"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {/* Password strength indicator */}
                  {password.length > 0 && (
                    <div className="flex items-center mt-2 text-xs">
                      {passwordValid ? (
                        <span className="flex items-center text-green-600">
                          <Check className="h-3 w-3 mr-1" />
                          At least 8 characters
                        </span>
                      ) : (
                        <span className="text-gray-400">
                          At least 8 characters
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Confirm password
                  </label>
                  <div className="relative">
                    <input
                      id="confirm-password"
                      name="confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="block w-full px-4 py-3 pr-11 border border-[#D4D4D8] rounded-[0.75rem] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2ECC71] focus:ring-1 focus:ring-[#2ECC71] transition-colors"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && (
                    <div className="flex items-center mt-2 text-xs">
                      {passwordsMatch ? (
                        <span className="flex items-center text-green-600">
                          <Check className="h-3 w-3 mr-1" />
                          Passwords match
                        </span>
                      ) : (
                        <span className="text-red-500">
                          Passwords do not match
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Error */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-[0.75rem] px-4 py-3">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading || !passwordValid || !passwordsMatch}
                  className="group w-full flex justify-center items-center py-3 px-4 text-sm font-semibold rounded-[0.75rem] text-white bg-[#2ECC71] hover:bg-[#27AE60] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2ECC71] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Resetting...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      Reset password
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  )}
                </button>
              </form>

              <p className="mt-8 text-center text-sm text-gray-500">
                <Link to="/login" className="inline-flex items-center font-medium text-[#2ECC71] hover:text-[#27AE60] transition-colors">
                  <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                  Back to sign in
                </Link>
              </p>
            </>
          ) : (
            /* Success state */
            <div className="text-center">
              <div className="mx-auto w-14 h-14 bg-[#ECFDF5] rounded-full flex items-center justify-center mb-5">
                <Check className="h-6 w-6 text-[#2ECC71]" />
              </div>
              <h2 className="font-['Satoshi'] text-2xl font-bold text-gray-900 tracking-tight">
                Password updated
              </h2>
              <p className="mt-3 text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
                Your password has been reset successfully. You can now sign in with your new password.
              </p>

              <div className="mt-8">
                <button
                  onClick={() => navigate('/login')}
                  className="group w-full flex justify-center items-center py-3 px-4 text-sm font-semibold rounded-[0.75rem] text-white bg-[#2ECC71] hover:bg-[#27AE60] transition-colors"
                >
                  <div className="flex items-center">
                    Sign in
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AbleResetPassword;
